import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../config";
import CompactYouTubePlayer from "../CompactYouTubePlayer";
import { formatSeconds, timeToSec } from "../../utils/time";
import {
  ALL_FIELDS,
  VOICE_FIELDS,
  CRITERIA_LABELS,
  computeComposite,
  rankComparator,
} from "../../utils/scoreComputation";
import {
  performanceCategorySlug,
  isVoiceSlug,
  CATEGORY_LABELS,
} from "../../utils/jeedigunta";

// Timestamp & Score Review (docs/backlog.md §1).
//
// One season-wide list of performances, ranked per award category by the
// Kathakala Agent composite, so the operator fixes timestamps where they
// matter most (top-10 red, top-15 orange, top-25 yellow). Saves go to
// performances.story_{start,end}_{time,sec} only; the Episode Editor's audit
// trigger records every change.
//
// Voice re-scoring is a paid Gemini call, so this tab never runs it. Saved
// rows join a re-score queue and the operator copies the
// pipeline/reanalyze_voice.py command to run by hand.

const REVIEW_CATEGORIES = [
  "galakala",
  "kalam_galam",
  "toli_kathanam_galam",
  "toli_kathanam_kalam",
];
const UNASSIGNED = "unassigned";

const PERF_COLUMNS = [
  "performance_id", "episode_id", "story_id", "storyteller_id",
  "storyteller_name_telugu", "storyteller_name_english", "category",
  "story_start_time", "story_end_time", "story_start_sec", "story_end_sec",
  "rating_storyteller", ...ALL_FIELDS,
  "episodes!inner(publish_date, video_id, video_duration_sec)",
].join(", ");

const MIN_STORY_SEC = 3 * 60;
const MAX_STORY_SEC = 45 * 60;
const QUEUE_KEY = "puraskaralu-review-rescore-queue";

// Word-order-agnostic name key — same rule the shortlist uses.
const nameKey = (raw) =>
  (raw || "").replace(/\s+/g, " ").trim().split(" ").filter(Boolean).sort().join(" ");

// Zero-padded HH:MM:SS, matching how story_start_time is stored.
const toHMS = (sec) => {
  if (sec == null || isNaN(sec)) return "";
  const s = Math.max(0, Math.floor(sec));
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
};

const rankTier = (rank) => {
  if (rank == null) return null;
  if (rank <= 10) return { row: "bg-red-50", badge: "bg-red-600 text-white" };
  if (rank <= 15) return { row: "bg-orange-50", badge: "bg-orange-500 text-white" };
  if (rank <= 25) return { row: "bg-yellow-50", badge: "bg-yellow-400 text-gray-900" };
  return { row: "", badge: "bg-gray-200 text-gray-700" };
};

// Reasons a stored timestamp pair looks wrong. `next` is the following
// performance in the same episode (by start time).
function timestampFlags(p, next) {
  const flags = [];
  const s = p.story_start_sec;
  const e = p.story_end_sec;
  if (s == null) flags.push("no start");
  if (e == null) flags.push("no end");
  if (s != null && e != null) {
    if (e <= s) flags.push("end ≤ start");
    else if (e - s < MIN_STORY_SEC) flags.push(`short (${Math.round((e - s) / 60)}m)`);
    else if (e - s > MAX_STORY_SEC) flags.push(`long (${Math.round((e - s) / 60)}m)`);
  }
  if (e != null && next?.story_start_sec != null && e > next.story_start_sec) {
    flags.push("overlaps next story");
  }
  const dur = p.episodes?.video_duration_sec;
  if (dur && ((s != null && s > dur) || (e != null && e > dur))) flags.push("past video end");
  return flags;
}

const hasVoice = (p) => VOICE_FIELDS.every((f) => p[f] != null);

async function fetchAllPerformances(from, to) {
  const pageSize = 1000;
  const rows = [];
  for (let offset = 0; ; offset += pageSize) {
    const { data, error } = await supabase
      .from("performances")
      .select(PERF_COLUMNS)
      .gte("episodes.publish_date", from)
      .lte("episodes.publish_date", to)
      .order("performance_id", { ascending: true })
      .range(offset, offset + pageSize - 1);
    if (error) throw error;
    rows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }
  return rows;
}

async function fetchStoryTitles(storyIds) {
  const map = {};
  for (let i = 0; i < storyIds.length; i += 200) {
    const { data, error } = await supabase
      .from("stories")
      .select("story_id, title_telugu, title_english")
      .in("story_id", storyIds.slice(i, i + 200));
    if (error) throw error;
    (data || []).forEach((s) => { map[s.story_id] = s.title_telugu || s.title_english || ""; });
  }
  return map;
}

const ScoreReviewTab = ({ season, ineligibleNamesTelugu = [] }) => {
  const [dateFrom, setDateFrom] = useState(season?.date_from || "");
  const [dateTo, setDateTo] = useState(season?.date_to || "");
  const [perfs, setPerfs] = useState([]);
  const [titles, setTitles] = useState({});
  const [shortlistIds, setShortlistIds] = useState(new Set());
  const [judges, setJudges] = useState([]);
  const [catTab, setCatTab] = useState("galakala");
  const [needsAttentionOnly, setNeedsAttentionOnly] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [drafts, setDrafts] = useState({}); // pid -> { start, end }
  const [savingId, setSavingId] = useState(null);
  const [activePid, setActivePid] = useState(null);
  const [videoId, setVideoId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const playerRef = useRef(null);
  const pendingSeekRef = useRef(null);

  const [queue, setQueue] = useState(() => {
    try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || "[]"); } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(queue)); } catch {}
  }, [queue]);

  useEffect(() => {
    setDateFrom(season?.date_from || "");
    setDateTo(season?.date_to || "");
  }, [season?.season_id, season?.date_from, season?.date_to]);

  const load = useCallback(async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    setError("");
    try {
      const [rows, slRes, judgesRes] = await Promise.all([
        fetchAllPerformances(dateFrom, dateTo),
        season
          ? supabase.from("jeedigunta_shortlist").select("performance_id").eq("season_id", season.season_id)
          : Promise.resolve({ data: [] }),
        supabase.from("storytellers").select("storyteller_id, storyteller_telugu").eq("is_judge", true),
      ]);
      if (slRes.error) throw slRes.error;
      if (judgesRes.error) throw judgesRes.error;
      setPerfs(rows);
      setShortlistIds(new Set((slRes.data || []).map((r) => r.performance_id)));
      setJudges(judgesRes.data || []);
      setTitles(await fetchStoryTitles([...new Set(rows.map((r) => r.story_id).filter(Boolean))]));
      setDrafts({});
    } catch (err) {
      console.error("Review load failed:", err);
      setError(err?.message || "Failed to load performances.");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, season]);

  useEffect(() => { load(); }, [load]);

  // Flags need each performance's successor within its episode.
  const flagsById = useMemo(() => {
    const byEpisode = {};
    perfs.forEach((p) => { (byEpisode[p.episode_id] ||= []).push(p); });
    const out = {};
    Object.values(byEpisode).forEach((list) => {
      const sorted = [...list].sort(
        (a, b) => (a.story_start_sec ?? Infinity) - (b.story_start_sec ?? Infinity)
      );
      sorted.forEach((p, i) => { out[p.performance_id] = timestampFlags(p, sorted[i + 1]); });
    });
    return out;
  }, [perfs]);

  // Rank within each category by the agent composite, skipping judges and
  // other ineligible storytellers exactly as the shortlist does.
  const grouped = useMemo(() => {
    const judgeIds = new Set(judges.map((j) => j.storyteller_id));
    const excluded = new Set(
      [...judges.map((j) => j.storyteller_telugu), ...ineligibleNamesTelugu].map(nameKey).filter(Boolean)
    );
    const groups = Object.fromEntries([...REVIEW_CATEGORIES, UNASSIGNED].map((c) => [c, []]));
    perfs.forEach((p) => {
      const slug = performanceCategorySlug(p.category);
      const ineligible =
        (p.storyteller_id && judgeIds.has(p.storyteller_id)) ||
        excluded.has(nameKey(p.storyteller_name_telugu));
      const criteria = Object.fromEntries(ALL_FIELDS.map((f) => [f, p[f]]));
      const { composite, voiceScore } = computeComposite([criteria], slug || "galakala");
      const voiceAvg = hasVoice(p)
        ? VOICE_FIELDS.reduce((acc, f) => acc + Number(p[f]), 0) / VOICE_FIELDS.length
        : null;
      const entry = {
        p,
        criteria,
        composite: slug ? composite : null,
        voiceScore: voiceScore ?? voiceAvg,
        ineligible,
        rank: null,
      };
      groups[slug && groups[slug] ? slug : UNASSIGNED].push(entry);
    });
    REVIEW_CATEGORIES.forEach((slug) => {
      const cmp = rankComparator(isVoiceSlug(slug));
      groups[slug].sort((a, b) => {
        if (a.ineligible !== b.ineligible) return a.ineligible ? 1 : -1;
        return cmp(a, b);
      });
      let rank = 0;
      groups[slug].forEach((e) => {
        if (!e.ineligible && e.composite != null) e.rank = ++rank;
      });
    });
    groups[UNASSIGNED].sort((a, b) => (b.voiceScore ?? -1) - (a.voiceScore ?? -1));
    return groups;
  }, [perfs, judges, ineligibleNamesTelugu]);

  const visible = useMemo(() => {
    const list = grouped[catTab] || [];
    if (!needsAttentionOnly) return list;
    // Backlog: "exclude agent-scored performances with accurate timestamps".
    return list.filter((e) => !(hasVoice(e.p) && (flagsById[e.p.performance_id] || []).length === 0));
  }, [grouped, catTab, needsAttentionOnly, flagsById]);

  // ---------- Player ----------
  const playAt = (p, sec) => {
    setActivePid(p.performance_id);
    const vid = p.episodes?.video_id;
    if (!vid) return;
    if (vid !== videoId) {
      pendingSeekRef.current = sec;
      setVideoId(vid);
    } else if (sec != null) {
      playerRef.current?.playFrom(sec);
    }
  };

  // The player only reports time once it is ready, so that is when a seek
  // queued for a newly loaded video can land.
  const handleTimeUpdate = useCallback((t) => {
    setCurrentTime(t);
    if (pendingSeekRef.current != null) {
      const sec = pendingSeekRef.current;
      pendingSeekRef.current = null;
      playerRef.current?.playFrom(sec);
    }
  }, []);

  // ---------- Editing ----------
  const draftFor = (p) =>
    drafts[p.performance_id] || {
      start: p.story_start_time || toHMS(p.story_start_sec),
      end: p.story_end_time || toHMS(p.story_end_sec),
    };

  const setDraft = (p, field, value) =>
    setDrafts((prev) => ({ ...prev, [p.performance_id]: { ...draftFor(p), [field]: value } }));

  const isDirty = (p) => {
    const d = drafts[p.performance_id];
    if (!d) return false;
    return timeToSec(d.start) !== p.story_start_sec || timeToSec(d.end) !== p.story_end_sec;
  };

  const save = async (p) => {
    const d = draftFor(p);
    const startSec = d.start ? timeToSec(d.start) : null;
    const endSec = d.end ? timeToSec(d.end) : null;
    if ((d.start && startSec == null) || (d.end && endSec == null)) {
      setError("Timestamps must look like HH:MM:SS.");
      return;
    }
    if (startSec != null && endSec != null && endSec <= startSec) {
      setError("End must be after start.");
      return;
    }
    setError("");
    setSavingId(p.performance_id);
    const patch = {
      story_start_time: startSec != null ? toHMS(startSec) : null,
      story_start_sec: startSec,
      story_end_time: endSec != null ? toHMS(endSec) : null,
      story_end_sec: endSec,
    };
    const { error: err } = await supabase
      .from("performances")
      .update(patch)
      .eq("performance_id", p.performance_id);
    setSavingId(null);
    if (err) {
      setError("Save failed: " + err.message);
      return;
    }
    setPerfs((prev) => prev.map((x) => (x.performance_id === p.performance_id ? { ...x, ...patch } : x)));
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[p.performance_id];
      return next;
    });
    setQueue((prev) => (prev.includes(p.performance_id) ? prev : [...prev, p.performance_id]));
    setNotice(`Saved #${p.performance_id} — added to the voice re-score queue.`);
    setTimeout(() => setNotice(""), 3000);
  };

  // ---------- Re-score command ----------
  const rescoreCommand = (dryRun) =>
    [
      "cd pipeline && python reanalyze_voice.py",
      `--date-from ${dateFrom} --date-to ${dateTo}`,
      `--pids ${queue.join(",")}`,
      // The script writes --snapshot even on a dry run, so only the real run takes it.
      dryRun ? "--dry-run" : `--snapshot review_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}`,
    ].filter(Boolean).join(" ");

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${label} copied.`);
    } catch {
      window.prompt("Copy this command:", text);
    }
    setTimeout(() => setNotice(""), 3000);
  };

  const counts = useMemo(
    () => Object.fromEntries(Object.entries(grouped).map(([k, list]) => [k, list.length])),
    [grouped]
  );

  const seekBtn = "px-2 py-1 border rounded text-xs font-mono hover:bg-gray-100";

  return (
    <div className="space-y-4">
      {/* Scope */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500">Episodes from</label>
          <input type="date" className="border rounded px-2 py-1 text-sm" value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-500">to</label>
          <input type="date" className="border rounded px-2 py-1 text-sm" value={dateTo}
            onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button onClick={load} className="border rounded px-3 py-1.5 text-sm hover:bg-gray-100">Reload</button>
        <label className="flex items-center gap-2 text-sm text-gray-700 ml-auto">
          <input type="checkbox" checked={needsAttentionOnly}
            onChange={(e) => setNeedsAttentionOnly(e.target.checked)} />
          Hide scored rows with clean timestamps
        </label>
        <p className="w-full text-xs text-gray-500">
          Defaults to the selected season's range; change the dates to review episodes outside any season.
          {season?.status === "closed" && (
            <span className="text-amber-700">
              {" "}This season is closed — timestamp fixes still save (they move the public player), but locked judge scores and results don't change.
            </span>
          )}
        </p>
      </div>

      {/* Player — stays pinned while scrolling the rows below */}
      <div className="sticky top-0 z-30 max-w-3xl mx-auto bg-kk-ink rounded-lg overflow-hidden shadow">
        {videoId ? (
          <CompactYouTubePlayer
            ref={playerRef}
            videoId={videoId}
            elementId="review-yt-player"
            sticky={false}
            onTimeUpdate={handleTimeUpdate}
          />
        ) : (
          <div className="text-center text-sm text-white/50 py-8">
            Press ▶ on a row to load its episode.
          </div>
        )}
        {videoId && (
          <div className="flex justify-center gap-1 pb-2">
            {[-30, -10, 10, 30].map((d) => (
              <button key={d} onClick={() => playerRef.current?.seekBy(d)}
                className="px-2 py-1 bg-white/5 border border-kk-gold/15 rounded text-kk-gold/70 text-[11px] font-mono hover:bg-kk-gold/10">
                {d > 0 ? `+${d}s` : `${d}s`}
              </button>
            ))}
          </div>
        )}
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2">{error}</div>}
      {notice && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded p-2">{notice}</div>}

      {/* Re-score queue */}
      {queue.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm flex flex-wrap items-center gap-2">
          <span className="font-semibold text-amber-900">
            Voice re-score queue: {queue.length}
          </span>
          <span className="text-amber-800 font-mono text-xs">{queue.join(", ")}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => copy(rescoreCommand(true), "Dry-run command")}
              className="border border-amber-300 rounded px-3 py-1 hover:bg-amber-100">
              Copy dry-run command
            </button>
            <button onClick={() => copy(rescoreCommand(false), "Re-score command (calls Gemini)")}
              className="border border-amber-300 rounded px-3 py-1 hover:bg-amber-100">
              Copy re-score command (paid)
            </button>
            <button onClick={() => setQueue([])} className="text-amber-700 hover:underline px-2">Clear</button>
          </div>
          <p className="w-full text-xs text-amber-800">
            Nothing runs from here. The re-score command calls Gemini for each queued performance and
            snapshots the current voice scores first; run the dry run to confirm audio is found.
          </p>
        </div>
      )}

      {/* Category tabs */}
      <div className="flex flex-wrap gap-1 bg-white rounded-lg shadow-sm p-1">
        {[...REVIEW_CATEGORIES, UNASSIGNED].map((slug) => (
          <button key={slug} onClick={() => setCatTab(slug)}
            className={`px-3 py-1.5 rounded-md text-sm font-semibold ${
              catTab === slug ? "bg-blue-600 text-white" : "text-gray-700 hover:bg-gray-100"
            }`}>
            {slug === UNASSIGNED ? "No category" : CATEGORY_LABELS[slug]?.short || slug}
            <span className="ml-1 text-xs opacity-70">({counts[slug] || 0})</span>
          </button>
        ))}
      </div>

      {catTab === UNASSIGNED && (
        <p className="text-xs text-gray-500">
          These have no award category yet, so they aren't ranked — sorted by voice score. Assign a
          category (Manage → Performances) to rank them.
        </p>
      )}

      {/* Rows */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs">
              <tr>
                <th className="px-2 py-2 text-left">Rank</th>
                <th className="px-2 py-2 text-left">Storyteller / story</th>
                {VOICE_FIELDS.map((f) => (
                  <th key={f} className="px-2 py-2">{CRITERIA_LABELS[f].telugu}</th>
                ))}
                <th className="px-2 py-2">R.గళం</th>
                <th className="px-2 py-2">Composite</th>
                <th className="px-2 py-2 text-left">Start / End</th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 && (
                <tr><td colSpan={10} className="text-center text-gray-400 py-8">
                  {needsAttentionOnly ? "Nothing needs attention here." : "No performances in this range."}
                </td></tr>
              )}
              {visible.map((e) => {
                const p = e.p;
                const pid = p.performance_id;
                const tier = rankTier(e.rank);
                const d = draftFor(p);
                const flags = flagsById[pid] || [];
                const active = activePid === pid;
                return (
                  <tr key={pid}
                    className={`border-t align-top ${tier?.row || ""} ${active ? "ring-2 ring-inset ring-blue-400" : ""}`}>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {e.rank != null ? (
                        <span className={`inline-block min-w-[2rem] text-center rounded px-1.5 py-0.5 text-xs font-bold ${tier.badge}`}>
                          {e.rank}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">{e.ineligible ? "ineligible" : "—"}</span>
                      )}
                      {shortlistIds.has(pid) && <span className="ml-1 text-amber-500" title="On this season's shortlist">★</span>}
                    </td>
                    <td className="px-2 py-2">
                      <div className="font-semibold text-gray-900">{p.storyteller_name_telugu || p.storyteller_name_english || "—"}</div>
                      <div className="text-gray-600">{titles[p.story_id] || ""}</div>
                      <div className="text-xs text-gray-400">
                        #{pid} · ep {p.episode_id} · {p.episodes?.publish_date}
                      </div>
                      {flags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {flags.map((f) => (
                            <span key={f} className="text-[11px] bg-red-100 text-red-700 rounded px-1.5">{f}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    {VOICE_FIELDS.map((f) => (
                      <td key={f} className="px-2 py-2 text-center tabular-nums">{p[f] ?? "—"}</td>
                    ))}
                    <td className="px-2 py-2 text-center tabular-nums">{p.rating_storyteller ?? "—"}</td>
                    <td className="px-2 py-2 text-center tabular-nums font-semibold">
                      {e.composite != null ? e.composite.toFixed(2) : "—"}
                    </td>
                    <td className="px-2 py-2">
                      {["start", "end"].map((field) => (
                        <div key={field} className="flex items-center gap-1 mb-1">
                          <input
                            className="w-24 border rounded px-1.5 py-0.5 font-mono text-xs"
                            placeholder="HH:MM:SS"
                            value={d[field]}
                            onChange={(ev) => setDraft(p, field, ev.target.value)}
                          />
                          <button
                            className={seekBtn}
                            disabled={!active || !videoId}
                            title={active ? `Use player time ${formatSeconds(currentTime)}` : "Press ▶ on this row first"}
                            onClick={() => setDraft(p, field, toHMS(currentTime))}
                          >
                            Set {field === "start" ? "Start" : "End"}
                          </button>
                          {timeToSec(d[field]) != null && (
                            <button className={seekBtn}
                              title={field === "start" ? "Play from start" : "Play the last 15s before end"}
                              onClick={() => playAt(p, field === "start" ? timeToSec(d.start) : Math.max(0, timeToSec(d.end) - 15))}>
                              ▶
                            </button>
                          )}
                        </div>
                      ))}
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">
                      {!active && (
                        <button className={seekBtn} onClick={() => playAt(p, p.story_start_sec ?? 0)}
                          disabled={!p.episodes?.video_id}>
                          ▶ Load
                        </button>
                      )}
                      {isDirty(p) && (
                        <button
                          className="ml-1 bg-blue-600 text-white rounded px-3 py-1 text-xs font-semibold hover:bg-blue-700 disabled:opacity-50"
                          disabled={savingId === pid}
                          onClick={() => save(p)}
                        >
                          {savingId === pid ? "Saving…" : "Save"}
                        </button>
                      )}
                      {queue.includes(pid) && <div className="text-[11px] text-amber-700 mt-1">queued for re-score</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ScoreReviewTab;
